import type { ReactiveController, ReactiveControllerHost } from 'lit'

import { NordEvent } from '../events.js'

export interface PopoutLike {
  show: () => Promise<unknown>
  hide: (moveFocusToButton?: boolean) => Promise<unknown>
}

export interface OpenStateOptions {
  /** Reads the host's `open` state. */
  isOpen: () => boolean
  /** Writes the host's `open` state. */
  setOpen: (open: boolean) => void
  /** The `nord-popout` to drive (queried lazily; may be undefined before render). */
  popout: () => PopoutLike | undefined
  /** True when the host can't be opened (disabled and/or read-only). */
  isInert?: () => boolean
  /** Side effects to run when the popout has opened (seed state, reset scroll…). */
  onOpen?: () => void
  /** Side effects to run when the popout has closed (clear search…). */
  onClose?: () => void
}

/**
 * Coordinates a host's `open` property with its `nord-popout` overlay, so all
 * the popout-backed components open/close consistently. Setting `open` drives
 * `popout.show()`/`hide()` (the popout only fires events / manages the top layer
 * through those, not via an attribute); the popout's own `open`/`close` events
 * sync `open` back and the host emits exactly one canonical `open`/`close`.
 * Opening is rejected while the host is inert (disabled / read-only).
 *
 * Wire the popout's events to {@link onPopoutOpen}/{@link onPopoutClose} in the
 * template, and call {@link show}/{@link hide}/{@link toggle} from triggers.
 */
export class OpenStateController implements ReactiveController {
  private previousOpen?: boolean

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options: OpenStateOptions,
  ) {
    host.addController(this)
  }

  hostUpdate() {
    // Reject an open while inert before render, so it folds into this cycle
    // (no extra update) and `hostUpdated` then keeps the popout closed.
    if (this.options.isOpen() && this.options.isInert?.()) {
      this.options.setOpen(false)
    }
  }

  hostUpdated() {
    const open = this.options.isOpen()
    if (open === this.previousOpen) {
      return
    }
    const first = this.previousOpen === undefined
    this.previousOpen = open

    // The first observed value is the settled initial state — don't drive the
    // popout or emit an event for it (and avoid touching the popout @query
    // before the first render).
    if (first) {
      return
    }

    // `open` is the source of truth: drive the popout and emit the one canonical
    // event here. show()/hide() are idempotent, so the round trip with
    // onPopoutClose (which syncs `open` when the popout self-closes) doesn't loop.
    if (open) {
      void this.options.popout()?.show()
      this.options.onOpen?.()
      this.host.dispatchEvent(new NordEvent('open'))
    }
    else {
      void this.options.popout()?.hide(false)
      this.options.onClose?.()
      this.host.dispatchEvent(new NordEvent('close'))
    }
  }

  /** Open the popout programmatically (no-op when already open or inert). */
  show() {
    if (this.options.isOpen() || this.options.isInert?.()) {
      return
    }
    this.options.setOpen(true)
  }

  /** Close the popout programmatically (no-op when already closed). */
  hide() {
    if (!this.options.isOpen()) {
      return
    }
    this.options.setOpen(false)
  }

  /** Toggle the popout (respecting the inert guard). */
  toggle() {
    if (this.options.isOpen()) {
      this.hide()
    }
    else {
      this.show()
    }
  }

  /**
   * Bind to the popout's `@open`. The canonical `open` is property-driven (see
   * `hostUpdated`) — the host opens via {@link show}/{@link toggle} from its
   * trigger — so this only swallows the popout's own bubbling event. (It must
   * NOT set `open` here: the popout's `open` fires asynchronously and could
   * otherwise re-open after a quick close.)
   */
  onPopoutOpen = (e: Event) => {
    e.stopPropagation()
  }

  /**
   * Bind to the popout's `@close`: swallow the popout's own event and sync
   * `open` so the host (via `hostUpdated`) emits the one canonical `close` when
   * the popout closes itself (e.g. light dismiss).
   */
  onPopoutClose = (e: Event) => {
    e.stopPropagation()
    this.options.setOpen(false)
  }
}
